import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { OrderResponseDTO, OrdersQueryDTO } from '@libs/dtos/order/order.dto';
import { CurrentUserService } from '@services/auth/current-user.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { OrdersService } from '@services/orders/orders.service';
import { PlatformService } from '@services/platforms/platform.service';
import { MenuService, PageTabs } from '@services/shared/menu.service';
import { TableBase } from '@shared/bases/table.base';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TableComponent } from '@shared/components/table/table.component';
import type { TagData } from '@shared/components/table/table.model';
import { TabHeader, TabsComponent } from '@shared/components/tabs/tabs.component';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale, formatDate, formatEur, formatTime } from 'app/core/utils/locale';
import { catchError, of } from 'rxjs';

import { paymentStatusTag, providerStatusTag } from './order-status';

/** An order row of the table: the list DTO with its display fields (price formatted, status tags...) */
type OrderRow = Omit<OrderResponseDTO, 'price'> & {
	price: string;
	orderSummary: string;
	platformName: string;
	userName: string;
	userEmail: string;
	paymentStatusLabel: TagData;
	providerStatusLabel: TagData;
	createdTime: string;
	mobileSummary: string;
};

@Component({
	selector: 'app-order',
	imports: [SharedModule, TableComponent, TabsComponent, ButtonComponent],
	templateUrl: './order.component.html',
	styleUrl: './order.component.scss',
})
export class OrderComponent extends TableBase implements OnInit, OnDestroy {
	private menuService = inject(MenuService);
	private translocoService = inject(TranslocoService);
	private messageAlertService = inject(MessageAlertService);

	data: OrderRow[] = [];
	userType: string | null = null;
	userId: number | undefined = undefined;
	platforms: { label: string; value: string }[] = [];

	// Role flags
	isAdmin = false;
	isClient = false;

	// Tabs configuration
	selectedTab = 0;
	tabs: TabHeader[] = [];

	// Default headers for admins
	defaultHeaders = [
		{
			field: 'orderSummary',
			header: this.translocoService.translate('orders.headers.order'),
			subField: 'platformName',
		},
		{ field: 'userName', header: this.translocoService.translate('orders.headers.user'), subField: 'userEmail' },
		{ field: 'price', header: this.translocoService.translate('orders.headers.price') },
		{
			field: 'paymentStatusLabel',
			header: this.translocoService.translate('orders.headers.paymentStatus'),
			type: 'tag' as const,
		},
		{
			field: 'providerStatusLabel',
			header: this.translocoService.translate('orders.headers.providerStatus'),
			type: 'tag' as const,
		},
		{ field: 'createdAt', header: this.translocoService.translate('orders.headers.date'), subField: 'createdTime' },
	];

	// Headers for clients (no provider status - they shouldn't see provider info)
	headersForClient = [
		{
			field: 'orderSummary',
			header: this.translocoService.translate('orders.headers.order'),
			subField: 'platformName',
		},
		{ field: 'price', header: this.translocoService.translate('orders.headers.price') },
		{
			field: 'paymentStatusLabel',
			header: this.translocoService.translate('orders.headers.status'),
			type: 'tag' as const,
		},
		{ field: 'createdAt', header: this.translocoService.translate('orders.headers.date'), subField: 'createdTime' },
	];

	// Current headers (changes based on selected tab)
	headers = this.defaultHeaders;

	// ====== Pagination Configuration ======
	override mainField = 'orderSummary';
	override sortField = 'createdAt';
	override sortOrder = -1;
	override limit = 10;
	loadError = false;

	constructor(
		private ordersService: OrdersService,
		private cdr: ChangeDetectorRef,
		private currentUserService: CurrentUserService,
		private platformService: PlatformService,
		private router: Router,
		override viewportService: ViewportService,
	) {
		super(viewportService);
	}

	openOrder(order: { id: number | string }): void {
		this.router.navigate(['/orders', order.id]);
	}

	override loadData(): void {
		this.loadOrders();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;

		this.userType = this.currentUserService.getUserRoleName();
		this.userId = this.currentUserService.getUserId();

		// Determine role
		// "All orders" scope comes from the permission (the backend decides the same way), not the role name
		this.isAdmin = this.currentUserService.hasPermission('orders:view-all');
		this.isClient = this.currentUserService.hasRole('client');

		// Configure tabs based on role
		this.configureTabs();
		this.publishPageTabs();

		// Set initial headers based on role
		if (this.isClient) {
			this.headers = this.headersForClient;
		}

		this.loadPlatforms();
		this.loadOrders();
	}

	// ====== Page tabs (topbar on desktop) ======

	private pageTabs: PageTabs | null = null;

	private publishPageTabs(): void {
		if (this.tabs.length === 0) return;
		this.pageTabs = {
			tabs: this.tabs,
			selected: this.selectedTab,
			onChange: (index) => this.onTabChange(index),
		};
		this.menuService.setPageTabs(this.pageTabs);
	}

	ngOnDestroy(): void {
		if (this.pageTabs) this.menuService.clearPageTabs(this.pageTabs);
	}

	configureTabs(): void {
		if (this.isAdmin) {
			this.tabs = [
				{ label: this.translocoService.translate('orders.tabs.all'), icon: 'List' },
				{ label: this.translocoService.translate('orders.tabs.mine'), icon: 'User' },
			];
		}
		// Client has no tabs - sees only their own orders
	}

	onTabChange(tabIndex: number): void {
		this.selectedTab = tabIndex;
		if (this.pageTabs) this.pageTabs.selected = tabIndex;
		this.page = 1;
		this.headers = this.isClient ? this.headersForClient : this.defaultHeaders;
		this.loadOrders();
	}

	/**
	 * Loads platforms for the filter dropdown
	 */
	loadPlatforms(): void {
		this.platformService.getPlatformOptions().subscribe({
			next: (options) => {
				this.platforms = options;
				this.filterFields = [
					{
						key: 'platformId',
						label: this.translocoService.translate('orders.filters.platform'),
						type: 'select' as const,
						options: this.platforms,
						placeholder: this.translocoService.translate('orders.filters.allPlatforms'),
					},
				];

				// Add userName filter only for non-client users
				if (!this.isClient) {
					this.filterFields.push({
						key: 'userName',
						label: this.translocoService.translate('orders.filters.user'),
						type: 'text' as const,
						placeholder: this.translocoService.translate('orders.filters.userPlaceholder'),
					});
				}

				this.cdr.detectChanges();
			},
			// The list still works without the platform filter; the user is told why it is missing
			error: () =>
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('common.states.platformsLoadError'),
				),
		});
	}

	/**
	 * Navigates to boost page to create a new order
	 */
	onCreateOrder(): void {
		this.router.navigate(['/boost']);
	}

	loadOrders(): void {
		this.isLoading = true;
		this.loadError = false;

		const queryParams: Partial<OrdersQueryDTO> & {
			platformId?: string;
			include_platform?: boolean;
			userName?: string;
		} = {
			page: this.page,
			limit: this.limit,
			sort_by: this.sortField,
			sort_order: this.sortOrder === 1 ? 'asc' : 'desc',
			include_user: true,
			include_product: true,
			include_platform: true,
		};

		// Add platform filter if selected
		if (this.currentFilters.platformId) {
			queryParams.platformId = this.currentFilters.platformId;
		}

		// Add userName filter if provided
		if (this.currentFilters.userName) {
			queryParams.userName = this.currentFilters.userName;
		}

		if (this.isAdmin) {
			// Tab 0 = all orders. Tab 1 = my orders.
			if (this.selectedTab === 1 && this.userId) {
				queryParams.userId = this.userId;
			}
		} else if (this.userId) {
			// Client - always filter by userId
			queryParams.userId = this.userId;
		}

		this.executeOrdersQuery(queryParams);
	}

	/**
	 * Execute the standard orders query
	 */
	private executeOrdersQuery(
		queryParams: Partial<OrdersQueryDTO> & { platformId?: string; include_platform?: boolean },
	): void {
		this.ordersService
			.getOrders(queryParams)
			.pipe(
				catchError((err) => {
					console.error('Error loading orders:', err);
					this.data = [];
					this.totalRecords = 0;
					this.isLoading = false;
					this.loadError = true;
					this.cdr.detectChanges();
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (response?.data) {
					const locale = activeLocale(this.translocoService);
					this.data = response.data.map((order) => ({
						...order,
						orderSummary: `${order.amount} ${order.product?.name || this.translocoService.translate('orders.fallback.product')}`,
						platformName: order.platform?.name || '-',
						userName: order.user ? `${order.user.name} ${order.user.lastName || ''}`.trim() : '-',
						userEmail: order.user?.email || '',
						price: formatEur(order.price ?? 0, locale),
						paymentStatusLabel: paymentStatusTag(order.paymentStatus, this.translocoService),
						providerStatusLabel: providerStatusTag(order.providerStatus, this.translocoService),
						createdAt: formatDate(order.createdAt, locale),
						createdTime: formatTime(order.createdAt, locale),
						// Collapsed mobile card: status + date under the product name
						mobileSummary: `${paymentStatusTag(order.paymentStatus, this.translocoService).label} · ${formatDate(order.createdAt, locale)}`,
					}));
					this.totalRecords = response.total;
				}
				this.isLoading = false;
				this.cdr.detectChanges();
			});
	}
}
